'use client';
import { usePathname } from 'next/navigation';
import { CmsProduct } from '../types/cmsProduct';
import { PushDataLayerFn } from '../types/pushDataLayer';
import {
  ComponentTypeEnum,
  sendProductAnalytics,
  UserActionEnum,
} from '../utils/analytics';
import { JsonObject } from '../utils/json';
import { sessionAttributionTracker } from '../utils/sessionAttributionTracking/sessionAttributionTracker';

type UseStickyFooterApplyClickParams = {
  product?: CmsProduct;
  /** dataLayer sink; when absent the CTA still navigates, just untracked. */
  pushDataLayer?: PushDataLayerFn;
  /** Event fields beyond the shared payload, e.g. TI's `search_result_parameter`. */
  extraData?: JsonObject;
};

/**
 * Click handler of a sticky footer's Apply CTA: records the apply attribution and fires CMS-1861
 * Event 3, Bottom Sticky · Apply Now (`product_click_apply` / `bottom-sticky`). Shared by the TI
 * provider page footer and the ProductDetails footer (CMS-2043), so every sticky footer reports the
 * same event. `item_list_name` is the page path, as on the sticky nav and the hero.
 */
export const useStickyFooterApplyClick = ({
  product,
  pushDataLayer,
  extraData,
}: UseStickyFooterApplyClickParams) => {
  const itemListName = usePathname() ?? undefined;

  return () => {
    sessionAttributionTracker.recordApplyClick();

    if (!product || !pushDataLayer) {
      return;
    }

    sendProductAnalytics({
      event: 'product_click_apply',
      action: UserActionEnum.CLICK,
      componentType: ComponentTypeEnum.BOTTOM_STICKY,
      componentTitle: 'Sticky Footer/CTA_Apply Now',
      index: '0',
      itemListName,
      product,
      extraData,
      pushDataLayer,
    });
  };
};
